/* tslint:disable:no-trailing-whitespace import-spacing no-inferrable-types typedef-whitespace */

import { Logger }            from "@/util/logger";
import { Scenario }          from "@/models/scenario/scenario";
import { DecisionPoint }     from "@/models/scenario/decision.point";

import {
  BufferAttribute,
  BufferGeometry,
  Color,
  LineDashedMaterial,
  LineSegments,
  Plane
} from 'three';

/**
 *
 * @class GridBuilder
 *
 * this builder renders the background grid
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      May 2023
 *
 */
export class GridBuilder {

  /** timescale gets clipped, so we don't go beyond simulation time */
  private  clippingPlane: Plane|null           = null;

  /** the geometric scale of the graph (vertical) */
  private    distanceScale: number             = 1.0;

  /** the geometric scale of the graph (horizontal) */
  private        timeScale: number             = 1.0;

  /** where the X data points start */
  private            dataX: number             = 0;

  /** where the Y data points start */
  private            dataY: number             = 0;

  /** the unscaled max time value */
  private        dataWidth: number             = 0;

  /** the unscaled max distance value */
  private       dataHeight: number             = 0;

  /** are we in dark or light mode? */
  private      nightMode: boolean        = true;

  /** the (maybe timezone specific) scenario */
  private         scenario: Scenario|null       = null;

  /** standard constructor */
  constructor(init?: Partial<GridBuilder>) {

    Object.assign(this, init);
  }

  /** force the clipping extent */
  public setClippingPlane(clip: Plane): GridBuilder {

    this.clippingPlane = clip;

    return this;
  }

  /** fetch the clipping extent */
  public getClippingPlane(): Plane {

    return this.clippingPlane;
  }

  /** force the data start (horizontal) */
  public setX(x: number): GridBuilder {

    this.dataX = x;

    return this;
  }

  /** fetch the data start (horizontal) */
  public getX(): number {

    return this.dataX;
  }

  /** force the data start (vertical) */
  public setY(y: number): GridBuilder {

    this.dataY = y;

    return this;
  }

  /** fetch the data start (vertical) */
  public getY(): number {

    return this.dataY;
  }

  /** force the data end (horizontal) */
  public setWidth(width: number): GridBuilder {

    this.dataWidth = width;

    return this;
  }

  /** fetch the data end (horizontal) */
  public getWidth(): number {

    return this.dataWidth;
  }

  /** force the data end (vertical) */
  public setHeight(height: number): GridBuilder {

    this.dataHeight = height;

    return this;
  }

  /** fetch the data end (vertical) */
  public getHeight(): number {

    return this.dataHeight;
  }

  /** force the distance (vertical) geometric scaling */
  public setDistanceScale(scale: number): GridBuilder {

    this.distanceScale = scale;

    return this;
  }

  /** fetch the distance (vertical) geometric scaling */
  public getDistanceScale(): number {

    return this.distanceScale;
  }

  /** force the time (horizontal) geometric scaling */
  public setTimeScale(scale: number): GridBuilder {

    this.timeScale = scale;

    return this;
  }

  /** fetch the time (horizontal) geometric scaling */
  public getTimeScale(): number {

    return this.timeScale;
  }

  /** force night mode  */
  public setNightMode(mode: boolean): GridBuilder {

    this.nightMode = mode;

    return this;
  }

  /** fetch night mode */
  public getNightMode(): boolean {

    return this.nightMode;
  }

  /** force the scenario */
  public setScenario(scenario: Scenario): GridBuilder {

    this.scenario = scenario;

    return this;
  }

  /** fetch the scenario */
  public getScenario(): Scenario {

    return this.scenario;
  }

  /** build the set of line segments as a complete geometry/mesh */
  public build(): LineSegments|null {

    let b: GridBuilder = this;

    if (!b.scenario) {

      Logger.error(`[GridBuilder] build - no scenario. Aborting.`);
      return null;
    }

    /* set up the 24h timebase grid lines */

    const gridColor: Color = new Color('#F0F0F0');

    if (!b.nightMode) {

      gridColor.set('#000000');
    }

    /** @note dashed lines are not working THREE.js, I tried many variations, can't get it to work. */

    let material: LineDashedMaterial = new LineDashedMaterial({
      color: gridColor,
      opacity: .1,
      transparent: true,
      linewidth: .5,
      dashSize: 3,
      gapSize: 1,
      clippingPlanes: [b.clippingPlane]
    });

    const       dps: DecisionPoint[] = b.scenario.milePoints(true);
    const       nDP: number          = dps.length;
    const      nDay: number          = ((b.dataWidth) / 24.0);
    let geometry: BufferGeometry     = new BufferGeometry();
    let positions: Float32Array      = new Float32Array((nDP * 2 * 3) + (nDay * 2 * 3));
    let indices: number[]            = [];
    let vIndex: number               = 0;
    let pIndex: number               = 0;

    geometry.name = 'grid';

    /* horizontal lines */

    for (let i = 0; i < nDP; i++) {

      const sx = 0;
      const sy = dps[i]._mileage;
      const ex = b.dataWidth;
      const ey = dps[i]._mileage;

      positions[vIndex]     = sx;
      positions[vIndex + 1] = sy;
      positions[vIndex + 2] = -0.001;

      positions[vIndex + 3] = ex;
      positions[vIndex + 4] = ey;
      positions[vIndex + 5] = -0.001;

      indices.push(pIndex);
      indices.push(pIndex + 1);

      vIndex += 6;
      pIndex += 2;

    }

    /* vertical lines */

    for (let i = 1; i < nDay; i++) {

      const sx = i * 24;
      const sy = 0;
      const ex = i * 24;
      const ey = b.dataHeight;

      if (sx >= b.dataWidth) {

        continue;
      }

      positions[vIndex]     = sx;
      positions[vIndex + 1] = sy;
      positions[vIndex + 2] = -0.001;

      positions[vIndex + 3] = ex;
      positions[vIndex + 4] = ey;
      positions[vIndex + 5] = -0.001;

      indices.push(pIndex);
      indices.push(pIndex + 1);

      vIndex += 6;
      pIndex += 2;
    }

    geometry.setAttribute('position', new BufferAttribute(positions, 3));
    geometry.setIndex(new BufferAttribute(new Uint32Array(indices), 1));

    /* all done */

    return new LineSegments(geometry, material);
  }

}
